import { Partner } from '../Forms/HotelForm/types';
import { useAddPackageExtraMutation } from '../graphql/addPackageExtra.generated';
import { useAddPackageHotelMutation } from '../graphql/addPackageHotel.generated';
import { useAddPackageInsuranceMutation } from '../graphql/addPackageInsurance.generated';
import { useAddPackageTicketMutation } from '../graphql/addPackageTicket.generated';
import { useAddPackageTransferMutation } from '../graphql/addPackageTransfer.generated';
import { useDeletePackageExtraMutation } from '../graphql/deletePackageExtra.generated';
import { useDeletePackageHotelMutation } from '../graphql/deletePackageHotel.generated';
import { useDeletePackageInsuranceMutation } from '../graphql/deletePackageInsurance.generated';
import { useDeletePackageTicketMutation } from '../graphql/deletePackageTicket.generated';
import { useDeletePackageTransferMutation } from '../graphql/deletePackageTransfer.generated';
import { GetPackageQueryResult, useGetPackageQuery } from '../graphql/getPackage.generated';
import { usePartnersQuery } from '../graphql/partners.generated';
import { useUpdatePackageMutation } from '../graphql/updatePackage.generated';
import { useUpdatePackageExtraMutation } from '../graphql/updatePackageExtra.generated';
import { useUpdatePackageHotelMutation } from '../graphql/updatePackageHotel.generated';
import { useUpdatePackageInsuranceMutation } from '../graphql/updatePackageInsurance.generated';
import { useUpdatePackageTicketMutation } from '../graphql/updatePackageTicket.generated';
import { useUpdatePackageTransferMutation } from '../graphql/updatePackageTransfer.generated';
import { useUpsertPackageCustomerPaymentMutation } from '../graphql/upsertPackageCustomerPayment.generated';
import { PackageAction, PackageActionType, PackageState } from './types';

const initialValues: PackageState = {
  hotels: [],
  transfers: [],
  insurances: [],
  tickets: [],
  extras: [],
  depositDate: new Date(),
  totalDate: new Date(),
  customerId: '',
  packageName: '',
  netTotal: 0,
  total: 0
};

const mapPackageToState = (data: GetPackageQueryResult['getPackage'] | null | undefined, partners: Partner[]): PackageState => {
  if (!data) {
    return initialValues;
  }

  const depositDate = data.customerPayment?.deposit?.date;
  const totalDate = data.customerPayment?.total?.date;
  console.log(data);
  return {
    hotels: data.hotels.map((hotel) => ({
      id: hotel.id,
      checkIn: new Date(+hotel.checkIn),
      checkOut: new Date(+hotel.checkOut),
      adults: hotel.adults || 0,
      children: hotel.children || 0,
      infants: hotel.infants || 0,
      roomType: hotel.roomType || '',
      hotel: hotel.hotel
        ? {
            id: hotel.hotel.id,
            name: hotel.hotel.name
          }
        : null,
      notes: hotel.notes || '',
      board: hotel.board || '',
      childrenAge: hotel.childrenAge || '',
      total: hotel.total || 0,
      partner: partners.find((partner) => partner.id === hotel.partner?.id) || null,
      view: hotel.view || '',
      extra: hotel.extra || ''
    })),
    transfers: data.transfers.map((transfer) => ({
      id: transfer.id,
      dates: transfer.dates.map((date) => new Date(+date)),
      company: transfer.company || '',
      partner: partners.find((partner) => partner.id === transfer.partner?.id) || null,
      total: transfer.total || 0,
      notes: transfer.notes || ''
    })),
    tickets: data.tickets.map((ticket) => ({
      id: ticket.id,
      departure: {
        date: new Date(+ticket.departure.date),
        reference: ticket.departure.reference || undefined
      },
      return: ticket.return?.date
        ? {
            date: new Date(+ticket.return.date),
            reference: ticket.return.reference || undefined
          }
        : undefined,
      numberOfPassengers: ticket.numberOfPassengers || 0,
      company: ticket.company || '',
      partner: partners.find((partner) => partner.id === ticket.partner?.id) || null,
      total: ticket.total || 0,
      notes: ticket.notes || ''
    })),
    insurances: data.insurances.map((insurance) => ({
      id: insurance.id,
      notes: insurance.notes || '',
      company: insurance.company || '',
      total: insurance.total || 0
    })),
    extras: data.extras.map((extra) => ({
      id: extra.id,
      name: extra.name || '',
      total: extra.total || 0,
      notes: extra.notes || ''
    })),
    depositDate: depositDate ? new Date(Number(depositDate)) : new Date(),
    totalDate: totalDate ? new Date(Number(totalDate)) : new Date(),
    customerId: data.customer?.id || '',
    packageName: data.packageName || '',
    netTotal: data.netTotal || 0,
    total: data.total || 0
  };
};

export const usePackage = (id: string) => {
  const { data, error, refetch } = useGetPackageQuery({ variables: { id }, fetchPolicy: 'no-cache' });
  const { data: partnersData } = usePartnersQuery();

  const partners = partnersData?.getPartners || [];

  const [addHotel] = useAddPackageHotelMutation();
  const [addTransfer] = useAddPackageTransferMutation();
  const [addInsurance] = useAddPackageInsuranceMutation();
  const [addTicket] = useAddPackageTicketMutation();
  const [addExtra] = useAddPackageExtraMutation();

  const [deleteHotel] = useDeletePackageHotelMutation();
  const [deleteTransfer] = useDeletePackageTransferMutation();
  const [deleteInsurance] = useDeletePackageInsuranceMutation();
  const [deleteTicket] = useDeletePackageTicketMutation();
  const [deleteExtra] = useDeletePackageExtraMutation();

  const [updateHotel] = useUpdatePackageHotelMutation();
  const [updateTransfer] = useUpdatePackageTransferMutation();
  const [updateInsurance] = useUpdatePackageInsuranceMutation();
  const [updateTicket] = useUpdatePackageTicketMutation();
  const [updateExtra] = useUpdatePackageExtraMutation();
  const [updatePackage] = useUpdatePackageMutation();
  const [upsertPackageCustomerPayment] = useUpsertPackageCustomerPaymentMutation();

  const dispatch = async (action: PackageAction) => {
    try {
      switch (action.type) {
        case PackageActionType.ADD_HOTEL:
          await addHotel({
            variables: {
              packageId: id,
              packageHotelInput: {
                ...action.payload,
                total: action.payload.total || 0,
                checkIn: action.payload.checkIn.toISOString(),
                checkOut: action.payload.checkOut.toISOString(),
                hotel: action.payload.hotel?.id || '',
                partner: action.payload.partner?.id || ''
              }
            }
          });
          break;

        case PackageActionType.ADD_TRANSFER:
          await addTransfer({
            variables: {
              packageId: id,
              packageTransferInput: {
                ...action.payload,
                dates: action.payload.dates.map((date) => date.toISOString()),
                total: action.payload.total || 0,
                partner: action.payload.partner?.id || ''
              }
            }
          });
          break;

        case PackageActionType.ADD_INSURANCE:
          await addInsurance({
            variables: {
              packageId: id,
              packageInsuranceInput: { ...action.payload, total: action.payload.total || 0 }
            }
          });
          break;

        case PackageActionType.ADD_TICKET:
          await addTicket({
            variables: {
              packageId: id,
              packageTicketInput: {
                ...action.payload,
                total: action.payload.total || 0,
                departure: { date: action.payload.departure.date.toISOString(), reference: action.payload.departure.reference },
                return: action.payload.return
                  ? { date: action.payload.return.date.toISOString(), reference: action.payload.return.reference }
                  : null,
                partner: action.payload.partner?.id
              }
            }
          });
          break;

        case PackageActionType.ADD_EXTRA:
          await addExtra({
            variables: {
              packageId: id,
              packageExtraInput: action.payload
            }
          });
          break;

        case PackageActionType.SET_PACKAGE_NAME:
          await updatePackage({
            variables: {
              id,
              input: { packageName: action.payload }
            }
          });
          break;

        case PackageActionType.REMOVE_HOTEL:
          await deleteHotel({
            variables: {
              packageId: id,
              hotelId: action.payload.id
            }
          });
          break;

        case PackageActionType.REMOVE_TRANSFER:
          await deleteTransfer({
            variables: {
              packageId: id,
              transferId: action.payload.id
            }
          });
          break;

        case PackageActionType.REMOVE_INSURANCE:
          await deleteInsurance({
            variables: {
              packageId: id,
              insuranceId: action.payload.id
            }
          });
          break;

        case PackageActionType.REMOVE_TICKET:
          await deleteTicket({
            variables: {
              packageId: id,
              ticketId: action.payload.id
            }
          });
          break;

        case PackageActionType.REMOVE_EXTRA:
          await deleteExtra({
            variables: {
              packageId: id,
              extraId: action.payload.id
            }
          });
          break;

        case PackageActionType.EDIT_HOTEL:
          await updateHotel({
            variables: {
              packageId: id,
              id: action.payload.id,
              packageHotelInput: {
                ...action.payload.values,
                total: action.payload.values.total || 0,
                checkIn: action.payload.values.checkIn.toISOString(),
                checkOut: action.payload.values.checkOut.toISOString(),
                hotel: action.payload.values.hotel?.id || '',
                partner: action.payload.values.partner?.id
              }
            }
          });
          break;

        case PackageActionType.EDIT_TRANSFER:
          await updateTransfer({
            variables: {
              packageId: id,
              id: action.payload.id,
              packageTransferInput: {
                ...action.payload.values,
                dates: action.payload.values.dates.map((date) => date.toISOString()),
                partner: action.payload.values.partner?.id
              }
            }
          });
          break;

        case PackageActionType.EDIT_INSURANCE:
          await updateInsurance({
            variables: {
              packageId: id,
              id: action.payload.id,
              packageInsuranceInput: { ...action.payload.values, total: action.payload.values.total || 0 }
            }
          });
          break;

        case PackageActionType.EDIT_TICKET:
          await updateTicket({
            variables: {
              packageId: id,
              id: action.payload.id,
              packageTicketInput: {
                ...action.payload.values,
                total: action.payload.values.total || 0,
                departure: {
                  date: action.payload.values.departure.date.toISOString(),
                  reference: action.payload.values.departure.reference
                },
                return: action.payload.values.return
                  ? { date: action.payload.values.return.date.toISOString(), reference: action.payload.values.return.reference }
                  : null,
                partner: action.payload.values.partner?.id || ''
              }
            }
          });
          break;

        case PackageActionType.EDIT_EXTRA:
          await updateExtra({
            variables: {
              packageId: id,
              id: action.payload.id,
              packageExtraInput: action.payload.values
            }
          });
          break;

        case PackageActionType.UPSERT_CUSTOMER_PAYMENT: {
          console.log({ payload: action.payload });
          const totalDate: string = action.payload.totalDate
            ? action.payload.totalDate.toISOString()
            : state.totalDate.toISOString();

          const depositDate: string = action.payload.depositDate
            ? action.payload.depositDate.toISOString()
            : state.depositDate.toISOString();
          await upsertPackageCustomerPayment({
            variables: {
              packageId: id,
              input: {
                depositDate,
                totalDate,
                totalAmount: action.payload.totalAmount || state.total
              }
            }
          });
          break;
        }
      }

      // Refetch data after successful mutation
      await refetch();
    } catch (error) {
      console.error('Error in package mutation:', error);
      throw error;
    }
  };

  const state = mapPackageToState(data?.getPackage, partners);
  console.log({ state, data });
  return { state, dispatch, packageDetails: data?.getPackage, refetch, error };
};
